/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { TableSelectionDetail, SortCriterionDetail } from '../UNSAFE_TableView';
import { TableColumn } from './Table';
import { TableHeaderCell } from './TableHeaderCell';
import { tableHeaderRowStyles, tableHeaderRowContainerStyles } from './themes/Table.css';

/**
 * Props for the TableHeader Component
 */
export type TableHeaderProps<K, D, C> = {
  tableId: string;
  columnsArray: TableColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  containsTabbableCell: boolean;
  focusedKey?: C;
  showFocusRing: boolean;
  selectedColumnKeys?: Keys<C>;
  selectedRowKeys?: Keys<K>;
  isColumnSelectionEnabled: boolean;
  isRowSelectionEnabled: boolean;
  isResizeHover: boolean;
  isRtl: boolean;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  sortCriterion?: { key: C; sortDirection: 'ascending' | 'descending' }[];
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
  startStickyEdge?: number;
  endStickyEdge?: number;
};

/**
 * The internal component used to render a header region in Table.
 */
export function TableHeader<K, D, C>({
  tableId,
  columnsArray,
  hasVerticalGridlines,
  focusedKey,
  showFocusRing,
  selectedColumnKeys,
  selectedRowKeys,
  isColumnSelectionEnabled,
  isRowSelectionEnabled,
  containsTabbableCell,
  onSelectionChange,
  sortCriterion,
  onSortCriterionChange,
  startStickyEdge,
  endStickyEdge,
  isResizeHover,
  isRtl
}: TableHeaderProps<K, D, C>) {
  const rowClasses = classNames([tableHeaderRowStyles.base]);
  const containerClasses = classNames([tableHeaderRowContainerStyles.base]);

  const getStyle = () => {
    return isResizeHover ? 'cursor: col-resize;' : '';
  };

  let isPreviousSelected = false;
  return (
    <thead class={containerClasses}>
      <tr class={rowClasses} role={'row'} style={getStyle()} data-oj-table-header-row={tableId}>
        {columnsArray.map((column: TableColumn<K, D, C>, columnIndex: number) => {
          const columnKey = column.key;
          let sortDirection;
          if (sortCriterion != null) {
            for (const criterion of sortCriterion) {
              if (criterion.key === columnKey) {
                sortDirection = criterion.sortDirection;
                break;
              }
            }
          }
          const isSelected = containsKey(selectedColumnKeys, columnKey);
          const headerCellProps = {
            tableId: tableId,
            hasVerticalGridlines: hasVerticalGridlines,
            columnKey: columnKey,
            columnIndex: columnIndex,
            isFinalColumnIndex: columnIndex === columnsArray.length - 1,
            stickyColumn: column.value.stickyEdge,
            isSelected: isSelected,
            isPreviousSelected: isPreviousSelected,
            selectedColumnKeys: selectedColumnKeys,
            selectedRowKeys: selectedRowKeys,
            isColumnSelectable: isColumnSelectionEnabled && column.value.selectable !== 'disabled',
            isRowSelectionEnabled: isRowSelectionEnabled,
            isFocusRingVisible: showFocusRing && columnKey === focusedKey && !containsTabbableCell,
            isTabbable: columnKey === focusedKey && containsTabbableCell,
            headerText: column.value.headerText,
            renderer: column.value.headerRenderer,
            onSelectionChange: onSelectionChange,
            sortable: column.value.sortable,
            sortDirection: sortDirection,
            onSortCriterionChange: onSortCriterionChange,
            isStickyStartEdge: columnIndex === startStickyEdge,
            isStickyEndEdge: columnIndex === endStickyEdge,
            isRtl: isRtl
          };
          isPreviousSelected = isSelected;
          return <TableHeaderCell {...headerCellProps} />;
        })}
      </tr>
    </thead>
  );
}
